// #30 Pill: a status word on its hue (DS §1.4, one meaning per hue). The tone comes from toneOf(word) when the text is
// one of his status words, or is given explicitly; a hue never carries meaning alone, the word is always on it.
// #76 IdPill: the Candidate ID (or another reference) in mono on the neutral tint; a click copies it.

import { useCallback, type ReactNode } from "react";
import { COMMON_LABELS, isStatusWord, toneOf, type Tone } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { Icon } from "./icons";

export interface PillProps {
  /** The status word (a label). A string that is one of his status words picks its own tone. */
  children: ReactNode;
  /** Overrides the word's tone; required for words that are not status words (default neutral). */
  tone?: Tone | undefined;
  lg?: boolean | undefined;
  /** The leading dot (default shown). */
  dot?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

/** The pill tone of a word: its duty when it is a status word, else neutral. */
export function pillTone(word: ReactNode, tone?: Tone | undefined): Tone {
  if (tone) return tone;
  return typeof word === "string" && isStatusWord(word) ? toneOf(word) : "neutral";
}

export function Pill({ children, tone, lg, dot = true, hs, className }: PillProps) {
  const hot = useHs(hs);
  const resolved = pillTone(children, tone);
  return (
    <span className={cx("mc-pill", resolved !== "neutral" && `mc-${resolved}`, lg && "mc-pill-lg", className)} {...hot}>
      {dot ? <i className="mc-pill-dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export interface IdPillProps {
  /** The reference itself, e.g. CAN-2026-000123. */
  value: string;
  /** The action's name (default *Copy Candidate ID*); also the tooltip. */
  label?: string | undefined;
  onCopy?: ((value: string) => void) | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

/** Writes to the clipboard when the browser allows it; never throws. */
export async function copyText(value: string): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !navigator.clipboard) return false;
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export function IdPill({ value, label = COMMON_LABELS.copyCandidateId, onCopy, can, hs, className }: IdPillProps) {
  const hot = useHs(hs);
  const copy = useCallback(() => {
    void copyText(value).then((done) => { if (done) onCopy?.(value); });
  }, [value, onCopy]);
  if (can === false) return <span className={cx("mc-pill", "mc-pill-mono", className)}>{value}</span>;
  return (
    <Tooltip label={label} mode="mirror">
      <button type="button" className={cx("mc-pill", "mc-pill-mono", "mc-idpill", className)} onClick={copy} {...hot}>
        <VisuallyHidden>{label}</VisuallyHidden>
        <span className="mc-mono">{value}</span>
        <Icon name="copy" size={14} />
      </button>
    </Tooltip>
  );
}
